import { appConfig } from '../../config'
import cookieStorage from '../cookie'
import { formatCountryOptionLabel, getCountryName, normalizeCountryCode } from '../../utils/countryUtils'

export const SUPPORT_EMAIL_COUNTRY_STORAGE_KEY = 'supportEmailCountryCode'
export const DEFAULT_SUPPORT_EMAIL_KEY = 'DEFAULT'

export const getWindow = () => (typeof window !== 'undefined' ? window : null)

const getConfiguredSupportCountries = supportEmailsByMarket =>
	Object.keys(supportEmailsByMarket || {})
		.map(normalizeCountryCode)
		.filter(countryCode => countryCode && countryCode !== DEFAULT_SUPPORT_EMAIL_KEY)
		.sort((left, right) => left.localeCompare(right))

const getDefaultSupportEmail = supportEmailsByMarket =>
	(supportEmailsByMarket || {})[DEFAULT_SUPPORT_EMAIL_KEY] ??
	(supportEmailsByMarket || {})[DEFAULT_SUPPORT_EMAIL_KEY.toLowerCase()] ??
	null

const getSupportEmailForCountry = (supportEmailsByMarket, countryCode) => {
	const normalizedCountryCode = normalizeCountryCode(countryCode)
	if (!normalizedCountryCode) return null

	const configuredKey = Object.keys(supportEmailsByMarket || {}).find(
		configuredCountryCode => configuredCountryCode.toUpperCase() === normalizedCountryCode,
	)

	return configuredKey ? supportEmailsByMarket[configuredKey] : null
}

const createSupportContactOption = ({ countryCode, label, email }) => ({
	countryCode,
	email,
	href: `mailto:${email}`,
	label,
})

export const getSupportEmailCountries = (config = appConfig) =>
	getConfiguredSupportCountries(config.supportEmailsByMarket)

export const getSupportEmailCountryOptions = (config = appConfig) =>
	getSupportEmailCountries(config).map(countryCode => ({
		label: formatCountryOptionLabel(countryCode),
		value: countryCode,
	}))

export const getSupportContactOptions = (config = appConfig) => {
	const supportEmailsByMarket = config.supportEmailsByMarket || {}
	const marketOptions = getSupportEmailCountries(config)
		.map(countryCode => {
			const email = getSupportEmailForCountry(supportEmailsByMarket, countryCode)
			return email
				? createSupportContactOption({
						countryCode,
						email,
						label: getCountryName(countryCode),
					})
				: null
		})
		.filter(Boolean)
	const defaultSupportEmail = getDefaultSupportEmail(supportEmailsByMarket)
	const defaultOption = defaultSupportEmail
		? [
				createSupportContactOption({
					countryCode: DEFAULT_SUPPORT_EMAIL_KEY,
					email: defaultSupportEmail,
					label: 'support',
				}),
			]
		: []

	return [...marketOptions, ...defaultOption]
}

export const getStoredSupportEmailCountry = () => {
	const windowObject = getWindow()
	return normalizeCountryCode(windowObject?.localStorage?.getItem(SUPPORT_EMAIL_COUNTRY_STORAGE_KEY))
}

export const setStoredSupportEmailCountry = countryCode => {
	const windowObject = getWindow()
	if (!windowObject?.localStorage) return

	const normalizedCountryCode = normalizeCountryCode(countryCode)
	if (normalizedCountryCode) {
		windowObject.localStorage.setItem(SUPPORT_EMAIL_COUNTRY_STORAGE_KEY, normalizedCountryCode)
	} else {
		windowObject.localStorage.removeItem(SUPPORT_EMAIL_COUNTRY_STORAGE_KEY)
	}
	windowObject.dispatchEvent(new Event('support-email-country-change'))
}

const getNavigatorLanguages = () => {
	const windowObject = getWindow()
	const navigatorObject = windowObject?.navigator
	if (!navigatorObject) return []

	if (Array.isArray(navigatorObject.languages)) {
		return navigatorObject.languages
	}

	return navigatorObject.language ? [navigatorObject.language] : []
}

const parseAcceptedLanguages = acceptLanguage =>
	String(acceptLanguage || '')
		.split(',')
		.map(item => {
			const [languageTag, ...parameters] = item.trim().split(';')
			const qualityParameter = parameters.find(parameter => parameter.trim().startsWith('q='))
			const quality = qualityParameter ? Number.parseFloat(qualityParameter.trim().slice(2)) : 1

			return {
				languageTag,
				quality: Number.isFinite(quality) ? quality : 1,
			}
		})
		.filter(({ languageTag }) => languageTag)
		.sort((left, right) => right.quality - left.quality)
		.map(({ languageTag }) => languageTag)

const getRegionFromLanguageTag = languageTag => {
	const parts = String(languageTag || '').split('-')
	const region = parts.slice(1).find(part => /^[A-Za-z]{2}$/.test(part))

	return normalizeCountryCode(region)
}

export const inferSupportCountryFromLanguages = ({
	acceptLanguage,
	navigatorLanguages = [],
	supportEmailsByMarket = {},
}) => {
	const supportedCountries = getConfiguredSupportCountries(supportEmailsByMarket)
	const languageTags = [...parseAcceptedLanguages(acceptLanguage), ...navigatorLanguages]

	return languageTags.reduce((result, languageTag) => {
		if (result) return result

		const region = getRegionFromLanguageTag(languageTag)
		return supportedCountries.includes(region) ? region : ''
	}, '')
}

export const resolveSupportEmail = ({
	config = appConfig,
	countryCode,
	currentMerchantCountryCode,
	storedCountryCode,
	navigatorLanguages,
} = {}) => {
	const supportEmailsByMarket = config.supportEmailsByMarket || {}
	const normalizedCountryCode = normalizeCountryCode(countryCode)
	const normalizedMerchantCountryCode = normalizeCountryCode(currentMerchantCountryCode)
	const normalizedStoredCountryCode = normalizeCountryCode(storedCountryCode)
	const inferredCountryCode = inferSupportCountryFromLanguages({
		acceptLanguage: config.requestAcceptLanguage,
		navigatorLanguages: navigatorLanguages || getNavigatorLanguages(),
		supportEmailsByMarket,
	})
	const selectedCountryCode =
		normalizedCountryCode || normalizedMerchantCountryCode || normalizedStoredCountryCode || inferredCountryCode
	const source =
		(normalizedCountryCode && 'explicit') ||
		(normalizedMerchantCountryCode && 'merchant') ||
		(normalizedStoredCountryCode && 'stored') ||
		(inferredCountryCode && 'inferred') ||
		'default'
	const configuredEmail =
		getSupportEmailForCountry(supportEmailsByMarket, selectedCountryCode) ||
		getDefaultSupportEmail(supportEmailsByMarket)
	const missingConfigLabel = selectedCountryCode
		? `Support email is not configured for ${selectedCountryCode}`
		: 'Support email is not configured'

	return {
		countryCode: selectedCountryCode,
		email: configuredEmail,
		href: configuredEmail ? `mailto:${configuredEmail}` : null,
		isConfigured: Boolean(configuredEmail),
		label: configuredEmail || missingConfigLabel,
		options: getSupportContactOptions(config),
		source,
	}
}

export const getSupportEmail = countryCode =>
	resolveSupportEmail({
		countryCode,
		currentMerchantCountryCode: cookieStorage.getItem('currentMerchantCountryCode'),
		storedCountryCode: getStoredSupportEmailCountry(),
	}).email
